import React, { useState, useEffect } from 'react';
import { Mail, Phone, MapPin, Globe, Star, Calendar, MessageSquare, Clock, Pencil } from 'lucide-react';
import EditLeadDialog from './EditLeadDialog';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Lead } from '@/types/lead';
import { cn } from '@/lib/utils';
import GoogleMap from '@/components/GoogleMap';
import { getLeadImages } from '@/services/leadImagesService';
import ImageGallery from '@/components/ImageGallery';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from '@/hooks/use-toast';
import { formatEmailForDisplay, getPrimaryEmail, extractWebsiteFromEmail } from '@/utils/emailUtils';
import { parseAddress } from '@/utils/addressParser';

interface BusinessOverviewProps {
  lead: Lead;
  onLeadUpdated?: () => void;
}

const BusinessOverview: React.FC<BusinessOverviewProps> = ({ lead, onLeadUpdated }) => {
  const [images, setImages] = useState(lead.images || []);
  const [refreshImages, setRefreshImages] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);

  useEffect(() => {
    const fetchImages = async () => {
      setIsLoading(true);
      try {
        const fetchedImages = await getLeadImages(lead.id);
        setImages(fetchedImages);
      } catch (error) {
        console.error('Error fetching images:', error);
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to load images"
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchImages();
  }, [lead.id, refreshImages]);

  const handleRefreshImages = () => {
    setRefreshImages(prev => prev + 1);
  };

  const handleEmailClick = () => {
    if (lead.email) {
      window.open(`mailto:${getPrimaryEmail(lead.email)}`);
    }
  };

  const handlePhoneClick = () => {
    if (lead.phone) {
      window.open(`tel:${lead.phone}`);
    }
  };

  const displayWebsite = lead.website || extractWebsiteFromEmail(lead.email);

  const handleWebsiteClick = () => {
    if (displayWebsite) {
      window.open(displayWebsite, '_blank', 'noopener,noreferrer');
    }
  };

  const handleAddressClick = () => {
    const hasValidCoords = typeof lead.latitude === 'number' && typeof lead.longitude === 'number' && isFinite(lead.latitude) && isFinite(lead.longitude);
    if (lead.placeId) {
      window.open(`https://www.google.com/maps/dir/?api=1&destination_place_id=${lead.placeId}`, '_blank', 'noopener,noreferrer');
    } else if (hasValidCoords) {
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${lead.latitude},${lead.longitude}`, '_blank', 'noopener,noreferrer');
    } else if (lead.address) {
      window.open(`https://maps.google.com/?q=${encodeURIComponent(lead.address)}`, '_blank', 'noopener,noreferrer');
    }
  };

  const formatPhoneNumber = (phone?: string) => {
    return phone || 'No phone number';
  };

  return (
    <div className="grid grid-cols-1 gap-4 md:gap-6">
      {/* Gallery Card - Now at the top */}
      <Card className="overflow-hidden" elevation="flat">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Gallery</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <ImageGallery 
            images={images} 
            leadId={lead.id} 
            onImageUpdate={handleRefreshImages} 
          />
        </CardContent>
      </Card>

      {/* Business Details Tabs */}
      <Card elevation="flat">
        <Tabs defaultValue="information" className="w-full">
          <CardHeader className="pb-0">
            <div className="flex items-center justify-between mb-2">
              <TabsList className="grid grid-cols-2">
                <TabsTrigger value="information">Business Information</TabsTrigger>
                <TabsTrigger value="location">Location</TabsTrigger>
              </TabsList>
              {onLeadUpdated && (
                <Button variant="ghost" size="icon" onClick={() => setIsEditOpen(true)} className="h-8 w-8">
                  <Pencil className="h-4 w-4" />
                </Button>
              )}
            </div>
          </CardHeader>

          {/* Business Information Tab */}
          <TabsContent value="information" className="p-0">
            <CardContent className="space-y-4">
              {lead.category && (
                <Badge variant="outline" className="capitalize">
                  {lead.category.replace(/_/g, ' ')}
                </Badge>
              )}
              
              <div className="space-y-2">
                <div className="flex items-start gap-2">
                  <MapPin className="h-4 w-4 text-muted-foreground mt-1 flex-shrink-0" />
                  <div className="space-y-1 flex-1">
                    <p className="text-sm font-medium leading-none">Address</p>
                    <p className="text-sm text-muted-foreground break-words">{lead.address || 'No address'}</p>
                    {lead.address && (
                      <Button 
                        variant="link" 
                        className="h-auto p-0 text-xs"
                        onClick={handleAddressClick}
                      >
                        View on Google Maps
                      </Button>
                    )}
                  </div>
                </div>
                
                <Separator />
                
                <div className="flex items-start gap-2">
                  <Phone className="h-4 w-4 text-muted-foreground mt-1 flex-shrink-0" />
                  <div className="space-y-1 flex-1">
                    <p className="text-sm font-medium leading-none">Phone</p>
                    <p className="text-sm text-muted-foreground break-words">{formatPhoneNumber(lead.phone)}</p>
                    {lead.phone && (
                      <Button 
                        variant="link" 
                        className="h-auto p-0 text-xs"
                        onClick={handlePhoneClick}
                      >
                        Call
                      </Button>
                    )}
                  </div>
                </div>
                
                <Separator />
                
                <div className="flex items-start gap-2">
                  <Mail className="h-4 w-4 text-muted-foreground mt-1 flex-shrink-0" />
                  <div className="space-y-1 flex-1">
                    <p className="text-sm font-medium leading-none">Email</p>
                    <p className="text-sm text-muted-foreground break-words">{formatEmailForDisplay(lead.email) || 'No email'}</p>
                    {lead.email && (
                      <Button 
                        variant="link" 
                        className="h-auto p-0 text-xs"
                        onClick={handleEmailClick}
                      >
                        Send Email
                      </Button>
                    )}
                  </div>
                </div>
                
                <Separator />
                
                <div className="flex items-start gap-2">
                  <Globe className="h-4 w-4 text-muted-foreground mt-1 flex-shrink-0" />
                  <div className="space-y-1 flex-1">
                    <p className="text-sm font-medium leading-none">Website</p>
                    <p className="text-sm text-muted-foreground break-words">{displayWebsite || 'No website'}</p>
                    {displayWebsite && (
                      <Button 
                        variant="link" 
                        className="h-auto p-0 text-xs"
                        onClick={handleWebsiteClick}
                      >
                        Visit Website
                      </Button>
                    )}
                  </div>
                </div>
                
                {lead.rating && (
                  <>
                    <Separator />
                    <div className="flex items-start gap-2">
                      <Star className={cn("h-4 w-4 mt-1 flex-shrink-0", 
                        lead.rating >= 4 ? "text-yellow-500 fill-yellow-500" : "text-muted-foreground")} />
                      <div className="space-y-1 flex-1">
                        <p className="text-sm font-medium leading-none">Rating</p>
                        <div className="flex items-center space-x-1">
                          <p className="text-sm text-muted-foreground">{lead.rating}</p>
                          {lead.reviewCount && (
                            <p className="text-xs text-muted-foreground">({lead.reviewCount} reviews)</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </>
                )}
                
                {lead.openingHours && (
                  <>
                    <Separator />
                    <div className="flex items-start gap-2">
                      <Clock className="h-4 w-4 text-muted-foreground mt-1 flex-shrink-0" />
                      <div className="space-y-1 flex-1">
                        <div className="flex items-center">
                          <p className="text-sm font-medium leading-none">Opening Hours</p>
                          {lead.openingHours.openNow !== undefined && (
                            <Badge variant={lead.openingHours.openNow ? "success" : "destructive"} className="ml-2 text-xs">
                              {lead.openingHours.openNow ? 'Open Now' : 'Closed'}
                            </Badge>
                          )}
                        </div>
                        {lead.openingHours.weekdayText && (
                          <div className="mt-1 text-xs text-muted-foreground space-y-1">
                            {lead.openingHours.weekdayText.map((day, index) => (
                              <p key={index}>{day}</p>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </CardContent>
          </TabsContent>

          {/* Location Tab */}
          <TabsContent value="location" className="p-0">
          <CardContent className="p-0 space-y-4">
            {/* Map Section */}
            {lead.address ? (
              <div className="w-full" style={{ height: '400px' }}>
            <GoogleMap
              location={lead.address}
              latitude={lead.latitude}
              longitude={lead.longitude}
              placeId={lead.placeId}
              height="400px"
            />
              </div>
            ) : (
                <div className="flex items-center justify-center h-[400px] bg-muted/30">
                  <div className="text-center space-y-2">
                    <MapPin className="h-12 w-12 text-muted-foreground mx-auto" />
                    <p className="text-muted-foreground">No map available</p>
                  </div>
                </div>
              )}

              {/* Location Details Section */}
              <div className="px-6 pb-6 space-y-4">
                {lead.address ? (
                  <>
                    {/* Full Address */}
                    <div className="flex items-start gap-3">
                      <MapPin className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
                      <div className="flex-1 space-y-2">
                        <p className="text-sm font-medium">Full Address</p>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {lead.address}
                        </p>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    const hasValidCoords = typeof lead.latitude === 'number' && typeof lead.longitude === 'number' && isFinite(lead.latitude) && isFinite(lead.longitude);
                    const url = lead.placeId
                      ? `https://www.google.com/maps/dir/?api=1&destination_place_id=${lead.placeId}`
                      : hasValidCoords
                        ? `https://www.google.com/maps/dir/?api=1&destination=${lead.latitude},${lead.longitude}`
                        : `https://maps.google.com/?q=${encodeURIComponent(lead.address)}`;
                    window.open(url, '_blank');
                  }}
                >
                  <MapPin className="h-4 w-4 mr-2" />
                  Get Directions
                </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => {
                              navigator.clipboard.writeText(lead.address || '');
                              toast({ title: "Address copied to clipboard" });
                            }}
                          >
                            Copy Address
                          </Button>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    {/* Parsed Address Components */}
                    {(() => {
                      const components = parseAddress(lead.address || '');
                      const hasComponents = Object.keys(components).length > 0;
                      
                      return hasComponents ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {components.street && (
                            <div className="space-y-1">
                              <p className="text-xs font-medium text-muted-foreground">Street</p>
                              <p className="text-sm">{components.street}</p>
                            </div>
                          )}
                          {components.city && (
                            <div className="space-y-1">
                              <p className="text-xs font-medium text-muted-foreground">City</p>
                              <p className="text-sm">{components.city}</p>
                            </div>
                          )}
                          {components.region && (
                            <div className="space-y-1">
                              <p className="text-xs font-medium text-muted-foreground">Region</p>
                              <p className="text-sm">{components.region}</p>
                            </div>
                          )}
                          {components.postalCode && (
                            <div className="space-y-1">
                              <p className="text-xs font-medium text-muted-foreground">Postal Code</p>
                              <p className="text-sm">{components.postalCode}</p>
                            </div>
                          )}
                          {components.country && (
                            <div className="space-y-1">
                              <p className="text-xs font-medium text-muted-foreground">Country</p>
                              <p className="text-sm">{components.country}</p>
                            </div>
                          )}
                        </div>
                      ) : null;
                    })()}

                    {/* Coordinates (if available) */}
                    {(lead.latitude && lead.longitude) && (
                      <>
                        <Separator />
                        <div className="space-y-2">
                          <p className="text-xs font-medium text-muted-foreground">Coordinates</p>
                          <div className="flex gap-4 text-sm">
                            <span>Lat: {lead.latitude.toFixed(6)}</span>
                            <span>Lng: {lead.longitude.toFixed(6)}</span>
                          </div>
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  <div className="text-center py-8 space-y-2">
                    <MapPin className="h-10 w-10 text-muted-foreground mx-auto" />
                    <p className="text-muted-foreground text-sm">No location information available</p>
                  </div>
                )}
              </div>
            </CardContent>
          </TabsContent>
        </Tabs>
      </Card>

      {onLeadUpdated && (
        <EditLeadDialog
          lead={lead}
          open={isEditOpen}
          onOpenChange={setIsEditOpen}
          onLeadUpdated={onLeadUpdated}
        />
      )}
    </div>
  );
};

export default BusinessOverview;
